import { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Box, Heading, Paragraph } from 'theme-ui';

import { withInView } from '@utils';
import { Section, Link } from '@snippets';

import { themed } from './SocialSharing.theme';
import { Schema } from './SocialSharing.schema';

const SCROLL_SPEED = 1;

export const SocialSharing = withInView(
  themed(({ theme, cms }) => {
    const { content, images, cta, section } = cms;

    const scrollRef = useRef(null);
    const animRef = useRef(null);
    const mainImagesRef = useRef(null);
    const hasAutoStarted = useRef(false);
    const isDragging = useRef(false);
    const dragStartX = useRef(0);
    const dragScrollLeft = useRef(0);

    const getScrollCycle = useCallback(() => {
      const el = scrollRef.current;
      if (!el) return 0;
      const c0 = el.children[0];
      const c1 = el.children[1];
      if (!c0 || !c1) return 0;
      return c1.offsetLeft - c0.offsetLeft;
    }, []);

    const normalizeInfiniteScroll = useCallback(() => {
      const el = scrollRef.current;
      if (!el) return;
      const cycle = getScrollCycle();
      if (cycle <= 0) return;
      while (el.scrollLeft >= cycle) {
        el.scrollLeft -= cycle;
      }
    }, [getScrollCycle]);

    const [isPlaying, setIsPlaying] = useState(false);
    const [isInView, setIsInView] = useState(false);
    const [cloneVisible, setCloneVisible] = useState(false);

    // Observe scroll container visibility for auto-scroll
    useEffect(() => {
      const el = scrollRef.current;
      const mainImagesEl = mainImagesRef.current;
      if (!el || !mainImagesEl) {
        return () => {
          /* scroll ref not mounted */
        };
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          const visible = entry.isIntersecting;
          setIsInView(visible);
          if (visible && !hasAutoStarted.current) {
            if (el.scrollWidth > el.clientWidth) {
              hasAutoStarted.current = true;
              setIsPlaying(true);
            }
          }
        },
        { threshold: 0.3 }
      );

      observer.observe(el);
      return () => observer.disconnect();
    }, []);

    // Auto-scroll animation loop
    useEffect(() => {
      if (!isPlaying || !isInView) {
        if (animRef.current) {
          cancelAnimationFrame(animRef.current);
          animRef.current = null;
        }
        return () => {
          /* isPlaying false or not in view */
        };
      }

      const scroll = () => {
        const el = scrollRef.current;
        if (!el) return;

        const maxScroll = el.scrollWidth - el.clientWidth;
        if (maxScroll <= 0) return;

        const cycle = getScrollCycle();
        if (cycle > 0) {
          el.scrollLeft += SCROLL_SPEED;
          if (el.scrollLeft >= cycle) {
            el.scrollLeft -= cycle;
          }
        } else if (el.scrollLeft >= maxScroll) {
          el.scrollLeft = 0;
        } else {
          el.scrollLeft += SCROLL_SPEED;
        }
        animRef.current = requestAnimationFrame(scroll);
      };

      animRef.current = requestAnimationFrame(scroll);
      return () => {
        if (animRef.current) {
          cancelAnimationFrame(animRef.current);
          animRef.current = null;
        }
      };
    }, [isPlaying, isInView, getScrollCycle]);

    // Drag-to-scroll for desktop mouse users
    useEffect(() => {
      const onMouseMove = (e) => {
        if (!isDragging.current || !scrollRef.current) return;
        e.preventDefault();
        const dx = e.pageX - dragStartX.current;
        scrollRef.current.scrollLeft = dragScrollLeft.current - dx;
        normalizeInfiniteScroll();
      };

      const onMouseUp = () => {
        isDragging.current = false;
        normalizeInfiniteScroll();
      };

      const onResize = () => {
        const mainImagesEl = mainImagesRef.current;
        const el = scrollRef.current;
        if (!mainImagesEl || !el) return;
        if (mainImagesEl.clientWidth > el.clientWidth) {
          setCloneVisible(true);
        } else {
          setCloneVisible(false);
        }
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      window.addEventListener('resize', onResize);
      return () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        window.removeEventListener('resize', onResize);
      };
    }, [normalizeInfiniteScroll]);

    const handleMouseDown = useCallback(
      (e) => {
        isDragging.current = true;
        dragStartX.current = e.pageX;
        dragScrollLeft.current = scrollRef.current?.scrollLeft || 0;
        if (isPlaying) setIsPlaying(false);
      },
      [isPlaying]
    );

    const handleTouchStart = useCallback(() => {
      if (isPlaying) setIsPlaying(false);
    }, [isPlaying]);

    const handleTouchEnd = useCallback(() => {
      normalizeInfiniteScroll();
    }, [normalizeInfiniteScroll]);

    const togglePlayPause = useCallback((e) => {
      e.stopPropagation();
      setIsPlaying((prev) => !prev);
    }, []);

    // Render one horizontal strip: big, [small, small], big, [small, small]...
    const renderImageStrip = (stripId, isAriaHidden) => {
      if (!images?.length) return null;

      const display = !isAriaHidden ? 'flex' : cloneVisible ? 'flex' : 'none';

      const elements = [];
      let i = 0;

      while (i < images.length) {
        // Big image (spans full height)
        const bigItem = images[i];
        if (bigItem?.image?.src) {
          elements.push(
            <Box key={`big-${stripId}-${i}`} sx={theme.tileBig}>
              <Box
                as="img"
                src={bigItem.image.src}
                alt={bigItem.alt || ''}
                loading="lazy"
                sx={theme.image}
              />
              {bigItem.name && (
                <Paragraph sx={theme.name}>{bigItem.name}</Paragraph>
              )}
            </Box>
          );
        }
        i += 1;

        // Two small stacked images
        if (i < images.length) {
          const small1 = images[i];
          const small2 = i + 1 < images.length ? images[i + 1] : null;

          elements.push(
            <Box key={`small-${stripId}-${i}`} sx={theme.smallPair}>
              {small1?.image?.src && (
                <Box sx={theme.tileSmall}>
                  <Box
                    as="img"
                    src={small1.image.src}
                    alt={small1.alt || ''}
                    loading="lazy"
                    sx={theme.image}
                  />
                  {small1.name && (
                    <Paragraph sx={theme.name}>{small1.name}</Paragraph>
                  )}
                </Box>
              )}
              {small2?.image?.src && (
                <Box sx={theme.tileSmall}>
                  <Box
                    as="img"
                    src={small2.image.src}
                    alt={small2.alt || ''}
                    loading="lazy"
                    sx={theme.image}
                  />
                  {small2.name && (
                    <Paragraph sx={theme.name}>{small2.name}</Paragraph>
                  )}
                </Box>
              )}
            </Box>
          );
          i += 2;
        }
      }

      return (
        <Box
          aria-hidden={isAriaHidden || undefined}
          sx={{
            display,
            flexShrink: 0,
            gap: '10px',
          }}
          ref={!isAriaHidden ? mainImagesRef : null}
        >
          {elements}
        </Box>
      );
    };

    return (
      <Section section={section}>
        <Container data-comp={SocialSharing.displayName} sx={theme.container}>
          <Box sx={theme.header}>
            {content?.superheading && (
              <Paragraph
                sx={{
                  ...theme.superheading,
                  color: content.superheadingColor || 'inherit',
                  textAlign: [
                    content.superheadingAlignMb || 'left',
                    null,
                    content.superheadingAlignDt || 'center',
                  ],
                }}
              >
                {content.superheading}
              </Paragraph>
            )}
            {content?.heading && (
              <Heading
                as="h2"
                sx={{
                  ...theme.heading,
                  color: content.headingColor || 'inherit',
                  textAlign: [
                    content.headingAlignMb || 'left',
                    null,
                    content.headingAlignDt || 'center',
                  ],
                }}
              >
                {content.heading}
              </Heading>
            )}
          </Box>

          <Box sx={theme.scrollWrapper}>
            <Box
              ref={scrollRef}
              sx={theme.scrollContainer}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              onScroll={normalizeInfiniteScroll}
            >
              {renderImageStrip('0', false)}
              {renderImageStrip('1', true)}
            </Box>

            <Box
              as="button"
              type="button"
              aria-label={isPlaying ? 'Pause auto-scroll' : 'Play auto-scroll'}
              onClick={togglePlayPause}
              sx={theme.playPauseBtn}
            >
              {isPlaying ? (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="currentColor"
                >
                  <rect x="1" y="1" width="4" height="12" rx="1" />
                  <rect x="9" y="1" width="4" height="12" rx="1" />
                </svg>
              ) : (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="currentColor"
                >
                  <path d="M2 1.5v11l10-5.5z" />
                </svg>
              )}
            </Box>
          </Box>

          {cta?.link?.text && (
            <Box sx={theme.ctaWrapper}>
              <Link
                href={cta.link.url}
                newTab={cta.link.newTab}
                variant={`buttons.${cta.style || 'primary'}`}
                sx={theme.ctaLink}
              >
                {cta.link.text}
              </Link>
            </Box>
          )}
        </Container>
      </Section>
    );
  }),
  { triggerOnce: true }
);

SocialSharing.displayName = 'Social Sharing';
SocialSharing.Schema = Schema;
